/**
 * SortableBlockList
 *
 * Wraps the canvas block list with drag-and-drop reordering via @dnd-kit/sortable.
 * Each block is wrapped in a SortableBlockItem that exposes a hover toolbar
 * with visibility toggle, clone, settings, and delete actions.
 */
import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Eye,
  EyeOff,
  Copy,
  Settings,
  Trash2,
  Plus,
} from 'lucide-react';
import type { BlockInstance } from './types';
// Block registry to resolve human-readable labels
import { getBlockEntry } from './componentRegistry';

/* ------------------------------------------------------------------ */
/*  SortableBlockItem                                                   */
/* ------------------------------------------------------------------ */

interface SortableBlockItemProps {
  id: string;
  blockKey: string;
  label: string;
  visible: boolean;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: (id: string) => void;
  onClone: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleVisible: (id: string) => void;
  children: React.ReactNode;
}

function SortableBlockItem({
  id,
  blockKey,
  label,
  visible,
  isSelected,
  isEditing,
  onSelect,
  onClone,
  onDelete,
  onToggleVisible,
  children,
}: SortableBlockItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative${isSelected ? ' ring-2 ring-primary rounded-sm' : ''}`}
    >
      {/* Floating toolbar — appears on hover */}
      <div className="absolute -top-9 left-0 right-0 z-10 flex items-center bg-[#242424] text-white rounded-t-md px-2 py-1 gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-white/10"
          title="Drag to reorder"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} />
        </button>

        {/* Block label */}
        <span
          className={`max-w-[120px] truncate select-none${!visible ? ' line-through opacity-60' : ''}`}
          title={label}
        >
          {label || blockKey}
        </span>

        <span className="flex-1" />

        {/* Toggle visibility */}
        <button
          className="p-0.5 rounded hover:bg-white/10"
          title={visible ? 'Hide block' : 'Show block'}
          onClick={(e) => { e.stopPropagation(); onToggleVisible(id); }}
        >
          {visible ? <Eye size={13} /> : <EyeOff size={13} />}
        </button>

        {/* Clone */}
        <button
          className="p-0.5 rounded hover:bg-white/10"
          title="Clone block"
          onClick={(e) => { e.stopPropagation(); onClone(id); }}
        >
          <Copy size={13} />
        </button>

        {/* Settings / select */}
        <button
          className="p-0.5 rounded hover:bg-white/10"
          title="Configure block"
          onClick={(e) => { e.stopPropagation(); onSelect(id); }}
        >
          <Settings size={13} />
        </button>

        {/* Delete */}
        <button
          className="p-0.5 rounded hover:bg-red-500/80"
          title="Delete block"
          onClick={(e) => { e.stopPropagation(); onDelete(id); }}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* Block content */}
      <div
        className={`${!visible ? 'opacity-50' : ''}`}
        onClick={() => onSelect(id)}
      >
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SortableBlockList                                                   */
/* ------------------------------------------------------------------ */

interface SortableBlockListProps {
  blocks: BlockInstance[];
  selectedBlockId: string | null;
  isEditing: boolean;
  onReorder: (newBlocks: BlockInstance[]) => void;
  onSelect: (id: string) => void;
  onClone: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleVisible: (id: string) => void;
  onInsertAfter?: (afterIndex: number) => void;
  renderBlock: (block: BlockInstance) => React.ReactNode;
}

export function SortableBlockList({
  blocks,
  selectedBlockId,
  isEditing: _isEditing,
  onReorder,
  onSelect,
  onClone,
  onDelete,
  onToggleVisible,
  onInsertAfter,
  renderBlock,
}: SortableBlockListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(blocks, oldIndex, newIndex).map((b, i) => ({
      ...b,
      order: i,
    }));

    onReorder(reordered);
  };

  const activeBlock = activeId ? blocks.find((b) => b.id === activeId) : null;

  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedBlocks.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-0">
          {sortedBlocks.map((block, idx) => {
            const entry = getBlockEntry(block.blockKey);
            const label = entry?.label ?? block.blockKey;
            return (
              <React.Fragment key={block.id}>
                {/* Insert-before zone at the very top */}
                {idx === 0 && onInsertAfter && (
                  <div className="group/insert relative flex items-center h-4 -mb-1">
                    <div className="flex-1 h-px bg-transparent group-hover/insert:bg-primary/40 transition-colors" />
                    <button
                      className="opacity-0 group-hover/insert:opacity-100 transition-opacity bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center shrink-0 shadow hover:scale-110 z-10"
                      onClick={(e) => { e.stopPropagation(); onInsertAfter(-1); }}
                      title="Insert block at top"
                    >
                      <Plus size={9} />
                    </button>
                    <div className="flex-1 h-px bg-transparent group-hover/insert:bg-primary/40 transition-colors" />
                  </div>
                )}

                <SortableBlockItem
                  id={block.id}
                  blockKey={block.blockKey}
                  label={label}
                  visible={block.visible}
                  isSelected={block.id === selectedBlockId}
                  isEditing={false}
                  onSelect={onSelect}
                  onClone={onClone}
                  onDelete={onDelete}
                  onToggleVisible={onToggleVisible}
                >
                  {renderBlock(block)}
                </SortableBlockItem>

                {/* Insert-after zone */}
                {onInsertAfter && (
                  <div className="group/insert relative flex items-center h-4 -mt-1">
                    <div className="flex-1 h-px bg-transparent group-hover/insert:bg-primary/40 transition-colors" />
                    <button
                      className="opacity-0 group-hover/insert:opacity-100 transition-opacity bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center shrink-0 shadow hover:scale-110 z-10"
                      onClick={(e) => { e.stopPropagation(); onInsertAfter(idx); }}
                      title="Insert block here"
                    >
                      <Plus size={9} />
                    </button>
                    <div className="flex-1 h-px bg-transparent group-hover/insert:bg-primary/40 transition-colors" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </SortableContext>

      {/* Drag overlay — shown while dragging */}
      <DragOverlay>
        {activeBlock ? (
          <div className="bg-[#242424]/90 text-white rounded-md px-3 py-2 text-xs font-medium shadow-lg flex items-center gap-2">
            <GripVertical size={14} />
            <span>
              {getBlockEntry(activeBlock.blockKey)?.label ?? activeBlock.blockKey}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableBlockList;
